import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import moment from 'moment'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import Pane from 'components/Common/Pane'
import { mediaQueryUp } from 'components/utils/breakpoint'
import BedbankBookingPane from './BedbankBookingPane'
import PaneBody from 'components/Common/Pane/PaneBody'
import useToggle from 'hooks/useToggle'
import OfferPageRoomGallery from '../OfferPageRoomGallery'
import BedbankPriceDetails from './BedbankPriceDetails'
import BedbankRoomFeatures from 'components/OfferPage/Bedbank/BedbankRoomFeatures'
import DetailsSection from 'components/Booking/Bedbank/DetailsSection'
import ImageWithReservedSpace from 'components/Common/ImageWithReservedSpace'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import { getPromotionDefaultPeriod } from 'lib/hotels/bedbankUtils'

import { NO_IMAGE_AVAILABLE_ID } from 'constants/image'
import SmallToggleButton from 'components/Luxkit/Button/SmallToggleButton'
import useFlightPrice from 'hooks/useFlightPrice'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import UrgencyLabel from 'components/Luxkit/Label/UrgencyLabel'
import BedbankPromotion from './BedbankPromotion'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import PaneDetailsSection from 'components/Common/Pane/PaneDetailsSection'
import { ImageParams } from 'components/Common/ResponsiveImage'
import Group from 'components/utils/Group'
import * as Analytics from 'analytics/analytics'
import { addToCartEvent } from 'analytics/optimizely/events'
import SolidBoltAltIcon from 'components/Luxkit/Icons/solid/SolidBoltAltIcon'
import { useInView } from 'react-intersection-observer'
import { fireNonInteractionEvent } from 'api/googleTagManager'
import { urgencyLabelImpression } from 'analytics/eventDefinitions'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import BedbankCancellationPolicy from './BedbankCancellationPolicy'
import BedbankOfferInclusions from './BedbankOfferInclusions'
import { take } from 'lib/array/arrayUtils'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import LineCheckIcon from 'components/Luxkit/Icons/line/LineCheckIcon'
import WalledContent from 'components/Common/WalledContent'
import useBedbankSelectDates from 'hooks/OfferPage/useBedbankSelectDates'

const TopWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: ${rem(224)};

  ${mediaQueryUp.tablet} {
    grid-template-rows: auto;
    grid-template-columns: ${rem(400)} 1fr;
  }
`

const RoomDeatils = styled.div`
  padding: ${rem(20)};
`

const PackageOptionsPackageRoomsWrapper = styled(PaneBody)`
  grid-column: span 3;
  overflow-anchor: none;

  > * {
    border-top: 1px solid ${({ theme }) => theme.palette.neutral.five};
  }
`

const ShowMoreContent = styled(BodyText)`
  padding: ${rem(12)};
`

const Description = styled(MarkdownRender)`
  p + p {
    margin-top: ${rem(4)};
  }
`

const PositionedBookmarkButton = styled.div`
  position: absolute;
  z-index: 1;
  top: ${rem(8)};
  right: ${rem(8)};

  ${mediaQueryUp.tablet} {
    top: ${rem(20)};
    right: ${rem(20)};
  }
`

const RoomContainer = styled(Pane)`
  position: relative;
`

const RateOption = styled.div`
  padding: ${rem(20)};
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(16)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr min-content;
    gap: ${rem(32)};
  }
`

const RateContent = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(16)};

  ${mediaQueryUp.desktop} {
    grid-template-columns: 4fr 6fr;
    gap: ${rem(36)};
  }
`

const RoomGallery = styled(OfferPageRoomGallery)`
  height: 100%;
  width: 100%;
`

const initialRatesToShow = 3
const imageProps: ImageParams = {
  desktopWidth: '400px',
  mobileWidth: '100vw',
  quality: 'good',
}

interface Props {
  checkinDate?: moment.Moment;
  checkoutDate?: moment.Moment;
  offer: App.BedbankOffer;
  packageView: App.BedbankOfferPackageView;
  rooms: Array<App.Occupants>;
  onBook: (pkg: App.BedbankPackage, rate: App.BedbankRate, withFlights?: boolean, flightPrice?: number) => void;
  flightsEnabled: boolean;
  fetchingRates?: boolean;
  isFilterActive: boolean;
  duration: number;
  bookedToday?: number;
}

function BedbankRoomOption(props: Props) {
  const {
    offer,
    packageView,
    rooms,
    checkinDate,
    checkoutDate,
    fetchingRates,
    flightsEnabled,
    isFilterActive,
    duration,
    bookedToday = 0,
    onBook: onBookProp,
  } = props

  const { value: showingMoreRates, toggle: showMoreRates } = useToggle(false)
  const { value: isShowingMoreDetails, toggle: toggleShowMoreDetails } = useToggle()
  const { airport } = useContext(OfferPageStateContext)

  const rates = packageView.rates
  const promotions = packageView.promotions
  const isSoldOut = !isFilterActive && !fetchingRates && rates.length == 0 && checkinDate && checkoutDate

  const hasImages = packageView.package.images.length > 0

  const isInitialPackageOption = promotions[rates[0]?.id]?.id === rates[0]?.id
  let flightCheckinDate, flightCheckoutDate, nights
  if (checkinDate && checkoutDate) {
    flightCheckinDate = checkinDate
    flightCheckoutDate = checkoutDate
    nights = checkoutDate.diff(checkinDate, 'days')
  }
  if (isInitialPackageOption) {
    const period = getPromotionDefaultPeriod()
    flightCheckinDate = period.startDate
    flightCheckoutDate = period.endDate
    nights = packageView.package.sell?.los
  }
  const [flightPrice] = useFlightPrice(offer.flights?.airportCode, rooms, flightCheckinDate, flightCheckoutDate, airport?.code, nights)

  const openDateSelector = useBedbankSelectDates()
  const onBook = useCallback((rate: App.BedbankRate, withFlights?: boolean) => {
    if (!checkinDate || !checkoutDate) {
      openDateSelector()
    } else {
      Analytics.track('add-to-cart', addToCartEvent())
      onBookProp(packageView.package, rate, withFlights, withFlights ? flightPrice : 0)
    }
  }, [checkinDate, checkoutDate, flightPrice, onBookProp, openDateSelector, packageView.package])

  const departureTooEarly = useMemo(() => {
    const minDay = 1
    return !!checkinDate && checkinDate.diff(moment(), 'days') < minDay
  }, [checkinDate])

  const overriddenDescription = !packageView.package.description.startsWith('\n**Size:**')

  const hasPromotions = offer.promotions?.length > 0

  const { ref: urgencyLabelsDesktopRef, inView: urgencyLabelsInView } = useInView({ triggerOnce: true })

  const bookedTodayLabel = bookedToday >= 3 && hasPromotions

  useEffect(() => {
    if (urgencyLabelsInView) {
      fireNonInteractionEvent(urgencyLabelImpression())
    }
  }, [urgencyLabelsInView])

  const canBuyNow = packageView.available && packageView.capacityValid && packageView.show && !fetchingRates

  return <RoomContainer shadow data-testid="PackageOption-bedbank">
    <CSSBreakpoint max="mobile">
      <TripGuard>
        <PositionedBookmarkButton>
          <BookmarkButton offer={offer} roomTypeId={packageView.package.roomId} />
        </PositionedBookmarkButton>
      </TripGuard>
    </CSSBreakpoint>
    <ScrollAnchor id={packageView.package.roomId} vOffset={rem(80)} />
    <TopWrapper>
      <div>
        {!hasImages && <ImageWithReservedSpace
          withPlaceholder
          id={NO_IMAGE_AVAILABLE_ID}
          {...imageProps}
          alt="Sorry this image is unavailable"
        />}
        {hasImages && <RoomGallery
          room={packageView.package}
          imageProps={imageProps}
        />}
      </div>
      <RoomDeatils>
        <Group direction="vertical" gap={12} tabletGap={16} verticalAlign="space-between">
          <VerticalSpacer gap={16}>
            <VerticalSpacer gap={8}>
              <Group direction="horizontal" horizontalAlign="space-between" gap={8}>
                <Heading variant="heading4">{packageView.package.name}</Heading>
                <CSSBreakpoint min="tablet">
                  <TripGuard>
                    <BookmarkButton offer={offer} roomTypeId={packageView.package.roomId} />
                  </TripGuard>
                </CSSBreakpoint>
              </Group>
              {bookedTodayLabel && <UrgencyLabel variant="critical1" ref={urgencyLabelsDesktopRef}>
                <Group direction="horizontal" verticalAlign="center" gap={4}>
                  <SolidBoltAltIcon size="XS" />
                  Booked {bookedToday} times today
                </Group>
              </UrgencyLabel>}
            </VerticalSpacer>
            { isSoldOut && <UrgencyLabel variant="critical2">SOLD OUT</UrgencyLabel>}
            <VerticalSpacer gap={4}>
              <Description fontSize="medium" content={packageView.package.description} />
              {!overriddenDescription && <BedbankRoomFeatures features={packageView.package.features} />}
            </VerticalSpacer>
          </VerticalSpacer>
          <BedbankBookingPane
            rates={rates}
            checkinDate={checkinDate}
            checkoutDate={checkoutDate}
            offer={offer}
            packageView={packageView}
            fetchingRates={fetchingRates}
            onShowMoreDetails={toggleShowMoreDetails}
          />
        </Group>
      </RoomDeatils>

    </TopWrapper>
    <PaneDetailsSection isOpen={isShowingMoreDetails} onToggle={toggleShowMoreDetails}>
      <DetailsSection
        pkg={packageView.package}
      />
    </PaneDetailsSection>
    <PackageOptionsPackageRoomsWrapper>
      {canBuyNow && rates.slice(0, showingMoreRates ? rates.length : initialRatesToShow).map((rate, i) =>
        <RateOption key={rate.id}>
          <RateContent>
            <div>
              {rate.corporate && (
                <ProductTypeLabel productType="corporate" />
              )}
              {promotions[rate.id] && <BedbankPromotion
                  promotion={promotions[rate.id]}
                  offer={offer}
                  duration={duration}
                  rooms={rooms}
                  rate={rate}
                  direction="column"
                  showNonCancellation={!isInitialPackageOption}
                  checkInDate={checkinDate}
                  checkOutDate={checkoutDate}
                />
              }
              {!promotions[rate.id] && <>
                <BodyText variant="large" weight="bold">Option {i + 1}</BodyText>
                <BedbankCancellationPolicy
                  rate={rate}
                  offer={offer}
                  showNonCancellation
                />
              </>}
            </div>
            <div>
              {promotions[rate.id] && <BedbankOfferInclusions
                content={promotions[rate.id].rateInclusionsLong}
                inclusions={promotions[rate.id].inclusions}
                maxHeight={180}
              />}
              {!promotions[rate.id] && <VerticalSpacer gap={4}>
                {take(rate.facilities, 30).concat(rate.promotions).map(inclusion =>
                  <BodyTextBlock key={inclusion} variant="medium" startIcon={<LineCheckIcon />}>
                    {inclusion}
                  </BodyTextBlock>,
                )}
              </VerticalSpacer>}
            </div>
          </RateContent>
          <WalledContent enforced={offer.walledGarden}>
            <BedbankPriceDetails
              promotions={promotions[rate.id]}
              rate={rate}
              rooms={rooms}
              offer={offer}
              checkIn={checkinDate}
              flightPrice={flightPrice}
              flightsEnabled={flightsEnabled}
              departureTooEarly={departureTooEarly}
              onBookNow={onBook}
            />
          </WalledContent>
        </RateOption>,
      )}
      {rates.length > initialRatesToShow &&
        <ShowMoreContent variant="medium" align="center">
          <SmallToggleButton
              on={showingMoreRates}
              onClick={showMoreRates}
            >
            {showingMoreRates ? 'Show less options' : 'Show all options'}
          </SmallToggleButton>
        </ShowMoreContent>
        }
    </PackageOptionsPackageRoomsWrapper>
  </RoomContainer>
}

export default BedbankRoomOption
